import { useRollbar } from '@rollbar/react'
import { Elements as StripeElements } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import { useQueryClient } from '@tanstack/react-query'
import 'moment-timezone'
import moment from 'moment/moment'
import { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { Navigate, Outlet, useLocation } from 'react-router'
import { ScrollRestoration } from 'react-router-dom'

import { useInit } from '../../hooks/useInit'
import { InvalidDataError } from '../../models/errors/InvalidDataError'
import { HistoryTrackerProvider } from '../../providers/HistoryTrackerProvider'
import { useNotificationListener } from '../../providers/NotificationsManagerProvider'
import { STRIPE_API_KEY } from '../../utils/constants'
import { Popover } from '../Popover'
import { RouterNavigationSpinner } from '../RouterNavigationSpinner'
import { Spinner } from '../Spinner'
import { WelcomeBackSelectPlanPage } from '../onboarding/WelcomeBackSelectPlanPage'
import { ErrorFallback } from './ErrorFallback'

const stripePromise = loadStripe(STRIPE_API_KEY)

const isUserLoggedIn = () => {
    return localStorage.getItem('loggedIn') === '1'
}

const App = () => {
    const { user, initComplete } = useInit()
    const rollbar = useRollbar()
    const queryClient = useQueryClient()
    const location = useLocation()

    const isOnLoginPage = location.pathname.startsWith('/login')

    const redirectUrl =
        window.location.pathname === '/'
            ? ''
            : `?redirectUrl=${encodeURIComponent(window.location.pathname)}`

    // Here we stick Rollbar on window so it can be accessed from SQLiteFactory.
    useEffect(() => {
        ;(window as any).pocketsuite_rollbar = rollbar
    }, [rollbar])

    useEffect(() => {
        if (user?.id === undefined) {
            return
        }
        rollbar.configure({ payload: { person: { id: user.id } } })
    }, [user?.id])

    useEffect(() => {
        document.getElementById('root-spinner')?.remove()
    }, [])

    useEffect(() => {
        if (user.timezone === undefined) {
            return
        }

        moment.tz.setDefault(user.timezone)
    }, [user.timezone])

    useNotificationListener('DashboardDataSourceChanged', async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['dashboard'],
                refetchType: 'all',
            }),
            queryClient.invalidateQueries({
                queryKey: ['calendar', 'external'],
            }),
            queryClient.invalidateQueries({
                queryKey: ['calendar', 'dashboard'],
            }),
        ])
    })

    useNotificationListener('ScheduleDataSourceChanged', async () => {
        await queryClient.invalidateQueries({
            queryKey: ['calendar', 'bookables'],
        })
    })

    useNotificationListener('PeopleDataSourceChanged', async () => {
        await queryClient.invalidateQueries({
            queryKey: ['clients'],
        })
    })

    useNotificationListener('ItemDataSourceChanged', async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['defaults'],
            }),
            queryClient.invalidateQueries({
                queryKey: ['categories'],
            }),
        ])
    })

    useNotificationListener('RecordDataSourceChanged', async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['defaults'],
            }),
            queryClient.invalidateQueries({
                queryKey: ['categories'],
            }),
        ])
    })

    useNotificationListener('ContractDataSourceChanged', async () => {
        await Promise.all([
            queryClient.invalidateQueries({
                queryKey: ['defaults'],
            }),
            queryClient.invalidateQueries({
                queryKey: ['categories'],
            }),
        ])
    })

    if (!isUserLoggedIn()) {
        return <Navigate to={`/login${redirectUrl}`} />
    }

    if (!initComplete) {
        return (
            <div className="flex h-screen w-screen items-center justify-center">
                <Spinner />
            </div>
        )
    }

    return (
        <ErrorBoundary
            FallbackComponent={ErrorFallback}
            onError={(error) => {
                if (!(error instanceof InvalidDataError)) {
                    rollbar.error(error)
                }
            }}
        >
            <Suspense fallback={<Spinner />}>
                <StripeElements stripe={stripePromise}>
                    <HistoryTrackerProvider>
                        {!isOnLoginPage && user?.requiresUpgrade ? (
                            <WelcomeBackSelectPlanPage />
                        ) : (
                            <>
                                <Outlet />
                                <ScrollRestoration />
                            </>
                        )}
                        <Popover />
                        <RouterNavigationSpinner />
                    </HistoryTrackerProvider>
                </StripeElements>
            </Suspense>
        </ErrorBoundary>
    )
}

export default App
